// ImportPatientsPanel.js - Enhanced with intelligent column matching

import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle2, Download, Eye, ChevronLeft, ChevronRight, MapPin, Settings, FileSpreadsheet, File } from 'lucide-react';
import { TREATMENT_TYPES, calcRecallDate } from '../utils/data';
import { format, parse, isValid } from 'date-fns';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

// ============================================================
// 1. INTELLIGENT COLUMN MATCHING DATABASE
// ============================================================

const COLUMN_MATCHING_DB = {
  // Patient name fields
  firstName: {
    keywords: ['first name', 'firstname', 'first', 'fname', 'given name', 'given', 'patient first', 'patient first name', 'first_name'],
    priority: 10,
    required: false, // Changed to false since we have fullName
  },
  lastName: {
    keywords: ['last name', 'lastname', 'last', 'lname', 'surname', 'family name', 'patient last', 'patient last name', 'last_name', 'family'],
    priority: 10,
    required: false, // Changed to false since we have fullName
  },
  fullName: {
    keywords: ['full name', 'fullname', 'patient name', 'name', 'patient full name', 'complete name', 'patient name', 'name'],
    priority: 10,
    required: true, // Changed to required
    parser: 'splitFullName',
  },
  
  // Contact fields
  phone: {
    keywords: ['phone', 'phone number', 'phonenumber', 'mobile', 'mobile number', 'cell', 'cell phone', 'telephone', 'tel', 'contact number', 'whatsapp', 'phone_no', 'phone_num', 'mobile_no'],
    priority: 9,
    required: false,
    parser: 'cleanPhone',
  },
  email: {
    keywords: ['email', 'email address', 'emailaddress', 'e-mail', 'mail', 'patient email', 'email_id', 'email_addr'],
    priority: 9,
    required: false,
  },
  
  // Date fields
  dob: {
    keywords: ['dob', 'date of birth', 'birthdate', 'birth date', 'birthday', 'date of birth', 'birth', 'patient dob', 'birth_day'],
    priority: 8,
    required: false,
    parser: 'parseDate',
  },
  lastVisit: {
    keywords: ['last visit', 'lastvisit', 'last visit date', 'visit date', 'appointment date', 'last appointment', 'last seen', 'recent visit', 'last_visit', 'visit', 'appointment', 'last_appointment', 'checkup date', 'last visit date'],
    priority: 9,
    required: true,
    parser: 'parseDate',
  },
  recallDate: {
    keywords: ['recall date', 'recall', 'next visit', 'next appointment', 'follow up', 'followup', 'recall', 'recall_due', 'next_visit', 'recall due'],
    priority: 7,
    required: false,
    parser: 'parseDate',
  },
  
  // Treatment fields
  treatmentType: {
    keywords: ['treatment', 'treatment type', 'procedure', 'service', 'treatment_name', 'procedure name', 'service type', 'dental treatment', 'work done', 'dental work', 'treatment_description', 'tx', 'procedure_type', 'treatment'],
    priority: 8,
    required: true,
    parser: 'matchTreatment',
  },
  
  // Additional fields
  notes: {
    keywords: ['notes', 'note', 'comments', 'remark', 'special notes', 'patient notes', 'additional info', 'comment', 'description', 'remarks'],
    priority: 6,
    required: false,
  },
  gender: {
    keywords: ['gender', 'sex', 'patient gender', 'gender_mf'],
    priority: 5,
    required: false,
  },
  address: {
    keywords: ['address', 'street', 'city', 'postal', 'zip', 'location', 'patient address'],
    priority: 5,
    required: false,
  },
};

// ============================================================
// 2. PARSER FUNCTIONS
// ============================================================

function cleanPhone(value) {
  if (!value) return '';
  // Remove all non-digit characters except +
  let cleaned = String(value).replace(/[^\d+]/g, '');
  // Add + if missing and it's a valid international number
  if (/^\d{10,15}$/.test(cleaned) && !cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }
  return cleaned;
}

function parseDate(value) {
  if (!value) return '';
  const str = String(value).trim();
  if (!str) return '';
  
  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  
  // Try various date formats
  const formats = [
    'yyyy-MM-dd',
    'dd/MM/yyyy',
    'MM/dd/yyyy',
    'dd-MM-yyyy',
    'MM-dd-yyyy',
    'dd.MM.yyyy',
    'MM.dd.yyyy',
    'yyyy/MM/dd',
    'dd MMM yyyy',
    'MMM dd yyyy',
  ];
  
  for (const fmt of formats) {
    try {
      const d = parse(str, fmt, new Date());
      if (isValid(d)) return format(d, 'yyyy-MM-dd');
    } catch (e) { /* continue */ }
  }
  
  // Try native Date parsing for other formats
  try {
    const d = new Date(str);
    if (isValid(d)) return format(d, 'yyyy-MM-dd');
  } catch (e) { /* ignore */ }
  
  return '';
}

// In ImportPatientsPanel.js - Update matchTreatment

function matchTreatment(value) {
  if (!value) return 'Custom';
  const norm = String(value).trim().toLowerCase();
  
  // Exact match against known treatment types
  const exact = Object.keys(TREATMENT_TYPES).find(t => t.toLowerCase() === norm);
  if (exact) return exact;
  
  // Enhanced treatment mapping with more keywords
  const treatmentMap = {
    'clean': 'Routine cleaning',
    'cleaning': 'Routine cleaning',
    'hygiene': 'Routine cleaning',
    'scale': 'Routine cleaning',
    'prophy': 'Routine cleaning',
    'routine exam': 'Routine cleaning',
    'exam': 'Routine cleaning',
    'prophylaxis': 'Routine cleaning',
    'ortho': 'Orthodontic',
    'braces': 'Orthodontic',
    'brace': 'Orthodontic',
    'aligner': 'Orthodontic',
    'invisalign': 'Orthodontic',
    'orthodontic': 'Orthodontic',
    'consultation': 'Orthodontic',
    'root canal': 'Root canal',
    'root': 'Root canal',
    'endo': 'Root canal',
    'canal': 'Root canal',
    'root canal therapy': 'Root canal',
    'retreatment': 'Root canal',
    'crown': 'Crown',
    'cap': 'Crown',
    'crown placement': 'Crown',
    'bridge': 'Bridge',
    'bridge preparation': 'Bridge',
    'cementation': 'Bridge',
    'implant': 'Implant',
    'implants': 'Implant',
    'dental implant': 'Implant',
    'implant evaluation': 'Implant',
    'white': 'Whitening',
    'bleach': 'Whitening',
    'whitening': 'Whitening',
    'teeth whitening': 'Whitening',
    'filling': 'Filling',
    'restoration': 'Filling',
    'composite': 'Filling',
    'composite filling': 'Filling',
    'bonding': 'Filling',
    'extraction': 'Extraction',
    'extract': 'Extraction',
    'surgical': 'Extraction',
    'surgical extraction': 'Extraction',
    'veneer': 'Veneer',
    'laminat': 'Veneer',
    'porcelain veneer': 'Veneer',
    'partial': 'Partial denture',
    'denture': 'Denture',
    'full denture': 'Denture',
    'denture reline': 'Denture',
    'full mouth debridement': 'Routine cleaning',
    'deep cleaning': 'Routine cleaning',
    'periodontal': 'Routine cleaning',
    'periodontal maintenance': 'Routine cleaning',
    'scaling': 'Routine cleaning',
    'scaling & root planing': 'Routine cleaning',
    'root planing': 'Routine cleaning',
    'nightguard': 'Custom',
    'tmj': 'Custom',
    'splinting': 'Custom',
    'sealant': 'Custom',
    'caries risk': 'Custom',
    'screening': 'Custom',
    'oral cancer': 'Custom',
    'evaluation': 'Custom',
    'post': 'Custom',
    'core build-up': 'Custom',
    'inlay': 'Custom',
    'restoration': 'Filling',
  };
  
  // Check for partial matches
  for (const [key, treatment] of Object.entries(treatmentMap)) {
    if (norm.includes(key)) return treatment;
  }
  
  return 'Custom';
}

// In ImportPatientsPanel.js - Update splitFullName

function splitFullName(value) {
  if (!value) return { firstName: '', lastName: '' };
  const trimmed = String(value).trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  
  // Split by spaces, but handle multi-word last names
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  
  // First part is first name, rest is last name
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  return { firstName, lastName };
}

function storeAsNote(value) {
  // Store unmatched fields as notes
  return value ? `[${key}]: ${value}` : '';
}

// ============================================================
// 3. MAIN IMPORT COMPONENT
// ============================================================

export default function ImportPatientsPanel({ onClose, onImport }) {
  const [step, setStep] = useState('upload'); // upload | mapping | preview | done
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mappedColumns, setMappedColumns] = useState({});
  const [unmatchedColumns, setUnmatchedColumns] = useState([]);
  const [parsedPatients, setParsedPatients] = useState([]);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [autoMapping, setAutoMapping] = useState(true);
  const rowsPerPage = 20;
  const fileInputRef = useRef(null);

  // Handle file upload
  const handleFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    setErrors([]);
    setWarnings([]);

    const reader = new FileReader();
    
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      // Parse Excel file
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
          
          if (jsonData.length === 0) {
            setErrors(['The Excel file appears to be empty.']);
            return;
          }
          
          const headers = Object.keys(jsonData[0]);
          const rows = jsonData.map(row => headers.map(h => String(row[h] || '').trim()));
          
          processData(headers, rows);
        } catch (error) {
          setErrors([`Error reading Excel file: ${error.message}`]);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Parse CSV
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          Papa.parse(text, {
            header: false,
            skipEmptyLines: true,
            trimHeaders: false,
            complete: (results) => {
              if (results.data.length === 0) {
                setErrors(['The CSV file appears to be empty.']);
                return;
              }
              const headers = results.data[0].map(h => String(h || '').trim());
              const rows = results.data.slice(1)
                .filter(row => row.some(cell => String(cell || '').trim() !== ''))
                .map(row => row.map(cell => String(cell || '').trim()));
              
              processData(headers, rows);
            },
            error: (error) => {
              setErrors([`Error parsing CSV: ${error.message}`]);
            }
          });
        } catch (error) {
          setErrors([`Error reading file: ${error.message}`]);
        }
      };
      reader.readAsText(file);
    }
  }, []);

  // Process data after parsing
  function processData(headers, rows) {
    setHeaders(headers);
    setDataRows(rows);
    
    // Auto-detect column mappings
    const { mapped, unmatched } = autoDetectColumns(headers);
    setMappedColumns(mapped);
    setUnmatchedColumns(unmatched);
    
    // Generate preview
    generatePreview(mapped, rows.slice(0, 50));
    setStep('mapping');
  }

  // Intelligent column detection
  function autoDetectColumns(headers) {
    const mapped = {};
    const unmatched = [];
    const usedKeywords = new Set();

    // Score each header against all columns
    const scoredHeaders = headers.map((header, index) => {
      const normalized = header.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
      const scores = [];
      
      for (const [key, config] of Object.entries(COLUMN_MATCHING_DB)) {
        if (key === 'custom') continue;
        let score = 0;
        let matchedKeyword = '';
        
        for (const keyword of config.keywords) {
          const normalizedKeyword = keyword.toLowerCase();
          if (normalized.includes(normalizedKeyword) || normalizedKeyword.includes(normalized)) {
            // Calculate score based on match quality
            if (normalized === normalizedKeyword) {
              score = config.priority + 3; // Exact match bonus
            } else if (normalized.includes(normalizedKeyword) || normalizedKeyword.includes(normalized)) {
              const lengthRatio = Math.min(normalized.length, normalizedKeyword.length) / Math.max(normalized.length, normalizedKeyword.length);
              score = config.priority * lengthRatio + 1;
            }
            matchedKeyword = keyword;
            break;
          }
        }
        
        scores.push({ key, score, matchedKeyword });
      }
      
      // Sort by score and get best match
      const best = scores.sort((a, b) => b.score - a.score)[0];
      return { header, index, ...best };
    });

    // Assign columns to highest scoring matches
    const sortedHeaders = scoredHeaders.sort((a, b) => b.score - a.score);
    
    for (const sh of sortedHeaders) {
      if (sh.score > 2 && !usedKeywords.has(sh.key)) {
        mapped[sh.key] = { index: sh.index, header: sh.header, confidence: sh.score };
        usedKeywords.add(sh.key);
      } else {
        // Store unmatched as custom
        if (sh.header && sh.header.trim()) {
          unmatched.push({ index: sh.index, header: sh.header });
        }
      }
    }

    // Ensure required columns are present
    for (const [key, config] of Object.entries(COLUMN_MATCHING_DB)) {
      if (config.required && !mapped[key]) {
        // Try to find a match with lower threshold
        const alternative = scoredHeaders.find(sh => !usedKeywords.has(sh.key) && sh.score > 0);
        if (alternative) {
          mapped[key] = { index: alternative.index, header: alternative.header, confidence: alternative.score };
          usedKeywords.add(key);
        }
      }
    }

    return { mapped, unmatched };
  }

  // Generate preview of parsed patients
  // In ImportPatientsPanel.js - Update generatePreview

function generatePreview(mapped, rows) {
  const parsed = [];
  const warnings = [];

  console.log('Mapped columns:', mapped); // Debug log
  console.log('Rows to parse:', rows.length); // Debug log

  rows.forEach((row, idx) => {
    try {
      const patient = {};
      let firstName = '';
      let lastName = '';
      
      // First pass: collect all mapped values
      for (const [key, config] of Object.entries(mapped)) {
        if (config.index === undefined || config.index >= row.length) continue;
        const value = row[config.index] || '';
        
        // Apply parser if exists
        const parser = COLUMN_MATCHING_DB[key]?.parser;
        let parsedValue = value;
        
        if (parser === 'parseDate') {
          parsedValue = parseDate(value);
        } else if (parser === 'cleanPhone') {
          parsedValue = cleanPhone(value);
        } else if (parser === 'matchTreatment') {
          parsedValue = matchTreatment(value);
        } else if (parser === 'splitFullName') {
          // Handle full name splitting
          const split = splitFullName(value);
          firstName = split.firstName;
          lastName = split.lastName;
          // Don't add to patient directly, we'll set after
          continue;
        } else if (parser === 'storeAsNote') {
          // Store as note later
        }
        
        // Store the parsed value
        if (key === 'firstName') {
          firstName = parsedValue;
        } else if (key === 'lastName') {
          lastName = parsedValue;
        } else {
          patient[key] = parsedValue;
        }
      }

      // Set first and last name
      patient.firstName = firstName || '';
      patient.lastName = lastName || '';

      // If we have no name, try to find any name column
      if (!patient.firstName && !patient.lastName) {
        warnings.push(`Row ${idx + 2}: Missing both first and last name. Skipped.`);
        return;
      }

      // Check for required fields
      if (!patient.lastVisit && !patient.recallDate) {
        warnings.push(`Row ${idx + 2} (${patient.firstName || 'Unknown'} ${patient.lastName || ''}): No visit date found. Using today's date.`);
        patient.lastVisit = format(new Date(), 'yyyy-MM-dd');
      }

      // Calculate recall date if not provided
      if (!patient.recallDate && patient.lastVisit && patient.treatmentType && patient.treatmentType !== 'Custom') {
        const d = calcRecallDate(patient.treatmentType, patient.lastVisit);
        if (d) patient.recallDate = format(d, 'yyyy-MM-dd');
      }

      // Store unmatched data as notes
      const unmatchedData = {};
      for (const u of unmatchedColumns) {
        const val = row[u.index] || '';
        if (val) {
          unmatchedData[u.header] = val;
        }
      }
      if (Object.keys(unmatchedData).length > 0) {
        const noteString = Object.entries(unmatchedData)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' | ');
        patient.notes = patient.notes ? `${patient.notes} | ${noteString}` : noteString;
      }

      parsed.push(patient);
    } catch (error) {
      warnings.push(`Row ${idx + 2}: Error processing - ${error.message}`);
    }
  });

  console.log('Parsed patients:', parsed.length); // Debug log
  setParsedPatients(parsed);
  setWarnings(warnings);
}

  // Manual column mapping
  function mapColumn(key, headerIndex) {
    const newMapped = { ...mappedColumns };
    // Remove old mapping for this key
    for (const [k, v] of Object.entries(newMapped)) {
      if (v.index === headerIndex) {
        delete newMapped[k];
      }
    }
    // Add new mapping
    if (headerIndex !== null && headerIndex !== undefined) {
      newMapped[key] = { 
        index: headerIndex, 
        header: headers[headerIndex],
        confidence: 100 // Manual mapping
      };
    }
    setMappedColumns(newMapped);
    
    // Update preview
    generatePreview(newMapped, dataRows.slice(0, 50));
  }

  // Download template
  function downloadTemplate() {
    const template = [
      'First Name,Last Name,Phone,Email,DOB,Last Visit Date,Treatment Type,Notes',
      'Sarah,Mitchell,+65 9123 4567,sarah.m@email.com,1985-04-12,2025-11-01,Routine cleaning,Prefers mornings',
      'James,Rodriguez,+65 9234 5678,james.r@email.com,1992-08-30,2026-05-25,Orthodontic,',
      'Aisha,Patel,+65 9345 6789,aisha.p@email.com,1988-11-15,2025-10-15,Routine cleaning,',
    ].join('\n');
    
    const blob = new Blob(['\uFEFF' + template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; 
    a.download = 'recallready-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // Confirm import
  async function handleConfirmImport() {
    setImporting(true);
    try {
      await onImport(parsedPatients);
      setStep('done');
    } catch (error) {
      setErrors([`Import failed: ${error.message}`]);
    } finally {
      setImporting(false);
    }
  }

  // Pagination
  const totalPages = Math.ceil(parsedPatients.length / rowsPerPage);
  const paginatedRows = parsedPatients.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Render upload step
  if (step === 'upload') {
    return (
      <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={panelStyle}>
          <div style={headerStyle}>
            <span style={titleStyle}>Import Patients</span>
            <button onClick={onClose} style={closeBtnStyle}><X size={20} /></button>
          </div>

          <div style={uploadContentStyle}>
            <div
              style={dropZoneStyle}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current.click()}
            >
              <Upload size={40} color="#1a3a3a" style={{ opacity: 0.4 }} />
              <div style={dropZoneTitleStyle}>Drop your file here, or click to browse</div>
              <div style={dropZoneSubStyle}>
                Supports CSV, Excel (.xlsx, .xls)
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])}
              />
            </div>

            {errors.length > 0 && (
              <div style={errorBoxStyle}>
                <AlertCircle size={16} color="#b85c4a" />
                <div>{errors[0]}</div>
              </div>
            )}

            <div style={templateSectionStyle}>
              <div style={templateHeaderStyle}>
                <FileText size={16} color="#7a9a92" />
                <span style={templateTitleStyle}>Need a template?</span>
              </div>
              <p style={templateDescStyle}>
                Download a sample CSV with the expected column format to get started quickly.
              </p>
              <button onClick={downloadTemplate} style={templateBtnStyle}>
                <Download size={14} />
                Download Template
              </button>
            </div>

            <div style={supportedFormatsStyle}>
              <span style={supportedTitleStyle}>Supported formats:</span>
              <span style={formatBadgeStyle}><FileSpreadsheet size={12} /> .xlsx</span>
              <span style={formatBadgeStyle}><FileSpreadsheet size={12} /> .xls</span>
              <span style={formatBadgeStyle}><File size={12} /> .csv</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render mapping step
  if (step === 'mapping') {
    const requiredColumns = Object.entries(COLUMN_MATCHING_DB)
      .filter(([key, config]) => config.required)
      .map(([key]) => key);

    return (
      <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{ ...panelStyle, width: 720 }}>
          <div style={headerStyle}>
            <div>
              <span style={titleStyle}>Map Columns</span>
              <div style={subtitleStyle}>{fileName} • {dataRows.length} rows</div>
            </div>
            <button onClick={() => setStep('upload')} style={closeBtnStyle}>
              <X size={20} />
            </button>
          </div>

          <div style={mappingContentStyle}>
            {errors.length > 0 && (
              <div style={errorBoxStyle}>
                <AlertCircle size={16} color="#b85c4a" />
                <div>{errors[0]}</div>
              </div>
            )}

            {/* Column mapping grid */}
            <div style={mappingGridStyle}>
              <div style={mappingHeaderStyle}>Your columns</div>
              <div style={mappingHeaderStyle}>Map to field</div>
              
              {headers.map((header, idx) => {
                const mappedKey = Object.entries(mappedColumns).find(([key, val]) => val.index === idx)?.[0];
                const isRequired = mappedKey && requiredColumns.includes(mappedKey);
                const config = mappedKey ? COLUMN_MATCHING_DB[mappedKey] : null;
                
                return (
                  <React.Fragment key={idx}>
                    <div style={columnHeaderStyle}>
                      <span>{header || `Column ${idx + 1}`}</span>
                    </div>
                    <div style={mappingSelectStyle}>
                      <select
                        value={mappedKey || ''}
                        onChange={(e) => mapColumn(e.target.value, e.target.value ? idx : null)}
                        style={selectStyle}
                      >
                        <option value="">— Skip column —</option>
                        {Object.entries(COLUMN_MATCHING_DB).map(([key, config]) => (
                          <option key={key} value={key}>
                            {key} {config.required ? '*' : ''}
                          </option>
                        ))}
                      </select>
                      {mappedKey && (
                        <span style={{
                          ...mappingBadgeStyle,
                          background: config?.required ? '#ecf5f2' : '#f5f8f7',
                          color: config?.required ? '#2d6a5e' : '#7a9a92',
                        }}>
                          {config?.required ? 'Required' : 'Optional'}
                        </span>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Preview */}
            <div style={previewSectionStyle}>
              <div style={previewHeaderStyle}>
                <span style={previewTitleStyle}>Preview ({parsedPatients.length} patients parsed)</span>
                {warnings.length > 0 && (
                  <span style={warningCountStyle}>{warnings.length} warnings</span>
                )}
              </div>
              <div style={previewTableWrapStyle}>
                <table style={previewTableStyle}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Phone</th>
                      <th>Last Visit</th>
                      <th>Treatment</th>
                      <th>Recall Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((p, i) => (
                      <tr key={i}>
                        <td>{(currentPage - 1) * rowsPerPage + i + 1}</td>
                        <td>{p.firstName || '—'}</td>
                        <td>{p.lastName || '—'}</td>
                        <td>{p.phone || '—'}</td>
                        <td>{p.lastVisit || '—'}</td>
                        <td>{p.treatmentType || '—'}</td>
                        <td>{p.recallDate || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div style={paginationStyle}>
                  <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={paginationBtnStyle}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span style={paginationTextStyle}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={paginationBtnStyle}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}

              {/* Warnings */}
              {warnings.length > 0 && (
                <div style={warningsStyle}>
                  <AlertCircle size={14} color="#d4a843" />
                  <div style={warningsListStyle}>
                    {warnings.slice(0, 5).map((w, i) => (
                      <div key={i} style={warningItemStyle}>{w}</div>
                    ))}
                    {warnings.length > 5 && (
                      <div style={warningMoreStyle}>+{warnings.length - 5} more warnings</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={footerStyle}>
              <button onClick={() => setStep('upload')} style={secondaryBtnStyle}>
                Choose different file
              </button>
              <button 
                onClick={handleConfirmImport} 
                style={primaryBtnStyle}
                disabled={parsedPatients.length === 0 || importing}
              >
                {importing ? 'Importing...' : `Import ${parsedPatients.length} patients`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render done step
  if (step === 'done') {
    return (
      <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={panelStyle}>
          <div style={headerStyle}>
            <span style={titleStyle}>Import Complete</span>
            <button onClick={onClose} style={closeBtnStyle}><X size={20} /></button>
          </div>
          <div style={doneContentStyle}>
            <CheckCircle2 size={56} color="#2d6a5e" />
            <div style={doneTitleStyle}>{parsedPatients.length} patients imported successfully</div>
            {warnings.length > 0 && (
              <div style={doneWarningStyle}>
                {warnings.length} warnings were generated. Check the patient list for details.
              </div>
            )}
            <button onClick={onClose} style={primaryBtnStyle}>
              View Patients
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ============================================================
// 4. STYLES
// ============================================================

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(26, 58, 58, 0.4)',
  zIndex: 1000,
  display: 'flex',
  justifyContent: 'flex-end',
};

const panelStyle = {
  background: '#ffffff',
  width: 580,
  maxWidth: '100vw',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '-4px 0 24px rgba(26, 58, 58, 0.12)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '20px 28px',
  borderBottom: '1px solid #e8ecea',
};

const titleStyle = {
  fontSize: 18,
  fontWeight: 600,
  color: '#1a3a3a',
  fontFamily: "'Inter', sans-serif",
};

const subtitleStyle = {
  fontSize: 13,
  color: '#7a9a92',
  marginTop: 2,
  fontFamily: "'Inter', sans-serif",
};

const closeBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#7a9a92',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
};

// Upload styles
const uploadContentStyle = {
  padding: '28px',
  flex: 1,
  overflowY: 'auto',
};

const dropZoneStyle = {
  border: '2px dashed #dce3e0',
  borderRadius: 12,
  padding: '48px 24px',
  textAlign: 'center',
  cursor: 'pointer',
  background: '#f8faf9',
  transition: 'border-color 0.2s, background 0.2s',
  ':hover': {
    borderColor: '#1a3a3a',
    background: '#f0f5f3',
  }
};

const dropZoneTitleStyle = {
  marginTop: 12,
  fontSize: 16,
  fontWeight: 500,
  color: '#1a3a3a',
  fontFamily: "'Inter', sans-serif",
};

const dropZoneSubStyle = {
  marginTop: 6,
  fontSize: 13,
  color: '#7a9a92',
  fontFamily: "'Inter', sans-serif",
};

const errorBoxStyle = {
  marginTop: 16,
  padding: '12px 16px',
  background: '#fdf0ed',
  border: '1px solid #f5d5ce',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 13,
  color: '#b85c4a',
  fontFamily: "'Inter', sans-serif",
};

const templateSectionStyle = {
  marginTop: 24,
  padding: '20px',
  background: '#f8faf9',
  borderRadius: 8,
  border: '1px solid #e8ecea',
};

const templateHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const templateTitleStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: '#1a3a3a',
  fontFamily: "'Inter', sans-serif",
};

const templateDescStyle = {
  marginTop: 6,
  fontSize: 13,
  color: '#7a9a92',
  fontFamily: "'Inter', sans-serif",
  lineHeight: 1.5,
};

const templateBtnStyle = {
  marginTop: 12,
  padding: '8px 16px',
  background: '#ffffff',
  color: '#1a3a3a',
  border: '1px solid #dce3e0',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.2s',
};

const supportedFormatsStyle = {
  marginTop: 20,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const supportedTitleStyle = {
  fontSize: 12,
  color: '#7a9a92',
  fontFamily: "'Inter', sans-serif",
};

const formatBadgeStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  background: '#f0f5f3',
  borderRadius: 6,
  fontSize: 12,
  color: '#3a5a52',
  fontFamily: "'Inter', sans-serif",
};

// Mapping styles
const mappingContentStyle = {
  padding: '24px 28px',
  flex: 1,
  overflowY: 'auto',
};

const mappingGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gap: '8px 16px',
  marginBottom: 24,
};

const mappingHeaderStyle = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#7a9a92',
  padding: '8px 0 4px',
  fontFamily: "'Inter', sans-serif",
  borderBottom: '1px solid #e8ecea',
};

const columnHeaderStyle = {
  padding: '6px 0',
  display: 'flex',
  alignItems: 'center',
  fontSize: 13,
  color: '#1a3a3a',
  fontFamily: "'Inter', sans-serif",
};

const mappingSelectStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '2px 0',
};

const selectStyle = {
  flex: 1,
  padding: '6px 10px',
  border: '1px solid #dce3e0',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1a3a3a',
  background: '#ffffff',
  outline: 'none',
};

const mappingBadgeStyle = {
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 10,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  whiteSpace: 'nowrap',
};

// Preview styles
const previewSectionStyle = {
  marginTop: 8,
  border: '1px solid #e8ecea',
  borderRadius: 8,
  overflow: 'hidden',
};

const previewHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  background: '#f8faf9',
  borderBottom: '1px solid #e8ecea',
};

const previewTitleStyle = {
  fontSize: 13,
  fontWeight: 500,
  color: '#1a3a3a',
  fontFamily: "'Inter', sans-serif",
};

const warningCountStyle = {
  fontSize: 12,
  color: '#d4a843',
  fontFamily: "'Inter', sans-serif",
};

const previewTableWrapStyle = {
  overflow: 'auto',
  maxHeight: 200,
};

const previewTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const paginationStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: '10px 0',
  borderTop: '1px solid #e8ecea',
};

const paginationBtnStyle = {
  padding: '4px 8px',
  background: 'transparent',
  border: '1px solid #dce3e0',
  borderRadius: 4,
  cursor: 'pointer',
  color: '#1a3a3a',
  display: 'flex',
  alignItems: 'center',
  ':disabled': {
    opacity: 0.3,
    cursor: 'not-allowed',
  }
};

const paginationTextStyle = {
  fontSize: 12,
  color: '#7a9a92',
  fontFamily: "'Inter', sans-serif",
};

const warningsStyle = {
  padding: '10px 16px',
  background: '#fef9f0',
  borderTop: '1px solid #f5e6ce',
  display: 'flex',
  gap: 10,
};

const warningsListStyle = {
  flex: 1,
  fontSize: 12,
  color: '#7a5a3a',
  fontFamily: "'Inter', sans-serif",
  maxHeight: 80,
  overflowY: 'auto',
};

const warningItemStyle = {
  padding: '2px 0',
};

const warningMoreStyle = {
  padding: '2px 0',
  color: '#9a8a7a',
};

const footerStyle = {
  display: 'flex',
  gap: 10,
  padding: '16px 28px',
  borderTop: '1px solid #e8ecea',
};

// Done styles
const doneContentStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px',
  textAlign: 'center',
};

const doneTitleStyle = {
  marginTop: 16,
  fontSize: 20,
  fontWeight: 600,
  color: '#1a3a3a',
  fontFamily: "'Inter', sans-serif",
};

const doneWarningStyle = {
  marginTop: 8,
  fontSize: 14,
  color: '#d4a843',
  fontFamily: "'Inter', sans-serif",
};

// Reuse primaryBtnStyle and secondaryBtnStyle
const primaryBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '10px 24px',
  background: '#1a3a3a',
  color: '#ffffff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.2s',
  flex: 1,
};

const secondaryBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '10px 24px',
  background: 'transparent',
  color: '#1a3a3a',
  border: '1px solid #dce3e0',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  transition: 'background 0.2s',
  flex: 1,
};